/**
 * Reads the acquirer's failure code out of `wallet_user_order.extra`.
 *
 * `extra` is an opaque JSON string whose shape belongs to whichever gateway handled
 * the payment — the domain has no type for it, and `Order.extra` is
 * `null | string`. This is the adapter that turns it into something the domain does
 * have: an error code.
 *
 * It lives here rather than in `FailureScreen` because a screen should not
 * `JSON.parse` a third-party payload and reach three levels into it. See
 * `.claude/rules/branching-logic.md` Rule 8.
 *
 * ⚠ `commitResult.result.errorCode` is the shape one acquirer sends. Other gateways
 * put their failure elsewhere in `extra`, so a `null` here means "no reason could be
 * read", never "the payment succeeded".
 *
 * Mapping the code to a message stays with the caller: choosing wording, and the
 * fallback for an unrecognised code, is presentation rather than parsing.
 */

interface CommitResultShape {
    commitResult?: {
        result?: {
            errorCode?: string | number;
        };
    };
}

/**
 * The acquirer's error code, or `null` when `extra` is absent, unparseable, or does
 * not carry one. Never throws.
 */
export const readPaymentFailureCode = (extra: string | null): string | null => {
    let parsed: CommitResultShape | null;

    try {
        parsed = JSON.parse(extra ?? 'null') as CommitResultShape | null;
    } catch {
        console.error(
            { module: 'order', action: 'parseExtra' },
            'Failed to parse order.extra',
        );
        return null;
    }

    const errorCode = parsed?.commitResult?.result?.errorCode;

    // Falsy covers absent, empty string and 0 — matching the original guard.
    return errorCode ? String(errorCode) : null;
};
